<template>
  <div>
    <p class="vacio" v-if="!filas || !filas.length">No hay ningún abono en este bloque.</p>
    <table class="tabla" v-else>
      <thead>
        <tr>
          <th>Cliente</th>
          <th>Abono</th>
          <th>Fecha</th>
          <th class="num">Días</th>
          <th class="num">Importe</th>
          <!-- El total al lado del importe, que es el saldo: el total dice QUÉ documentos rectifica
               el abono y el saldo cuánto queda por saldar. Son dos preguntas distintas y las dos se
               tienen que ver (dirección, 16/10/2026). No se suma en el pie: la cifra de cabecera es
               la de los saldos (D-COB-2). -->
          <th class="num">Total</th>
          <th>Empleado de ventas</th>
          <th>Factura(s) sugeridas para casar</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="fila in filas" :key="fila.id || fila.codigo">
          <td>{{ texto(fila.cliente) }}</td>
          <td>{{ documento(fila) }}</td>
          <td>{{ formatFecha(fila.fechaEmision) }}</td>
          <td class="num">{{ numero(fila.diasAntiguedad) }}</td>
          <td class="num">{{ formatEuros(fila.importe) }}</td>
          <td class="num">{{ formatEuros(fila.total) }}</td>
          <td>{{ texto(fila.empleadoVentas) }}</td>
          <td>
            <!-- Las salidas de la columna dicen lo que son, y quién decide cuál es el servidor
                 (`sugerencia`): aquí NO se deduce contando las listas, porque deducirlo otra vez es
                 donde esta pantalla y el correo pueden acabar rotulando distinto el mismo abono.
                 Una respuesta con el estado y las listas en desacuerdo se rotula por el estado. -->
            <template v-if="esNoDeducible(fila)">
              <span class="marca marca-aviso">{{ texto(fila.sugerenciaEtiqueta) }}</span>
              <p class="motivo" v-if="fila.motivoNoDeducible">{{ fila.motivoNoDeducible }}</p>
            </template>
            <template v-else>
              <!-- El rótulo del estado se marca siempre menos en la candidata única, que se lee
                   sola. Lo decide el ESTADO, igual que la rama: «Varias posibles», «Conjunto que
                   suma el total del abono» y «Varios conjuntos posibles» vienen escritos del
                   servidor, que es lo que hace que el correo los diga con las mismas palabras. -->
              <span class="marca marca-aviso" v-if="!esUnica(fila)">
                {{ texto(fila.sugerenciaEtiqueta) }}
              </span>
              <p class="candidata" v-for="candidata in (fila.facturasSugeridas || [])"
                :key="candidata.id || candidata.codigo">
                {{ documento(candidata) }} · {{ formatFecha(candidata.fechaEmision) }} ·
                {{ formatEuros(candidata.saldoPendiente) }}
              </p>

              <!-- #537 · EL CONJUNTO DE FACTURAS QUE SUMA EL TOTAL DEL ABONO.
                   Se pinta siempre que venga, también cuando hay candidatas sueltas: el servidor
                   solo lo manda cuando lo hay, y cuál es la respuesta más directa lo dice el
                   rótulo, no el orden. Cada conjunto lleva el total y el saldo de cada factura y
                   las dos sumas, que es lo que permite rehacer la cuenta que lo justifica. -->
              <div class="conjunto" v-for="(conjunto, i) in (fila.conjuntosSugeridos || [])"
                :key="'cj-' + i">
                <p class="conjunto-titulo">{{ texto(conjunto.etiqueta) }}</p>
                <p class="candidata" v-for="factura in (conjunto.facturas || [])"
                  :key="factura.id || factura.codigo">
                  {{ documento(factura) }} · {{ formatFecha(factura.fechaEmision) }} ·
                  total {{ formatEuros(factura.total) }} ·
                  saldo {{ formatEuros(factura.saldoPendiente) }}
                </p>
                <p class="conjunto-sumas">
                  Suman {{ formatEuros(conjunto.totalFacturas) }} de total y
                  {{ formatEuros(conjunto.saldoPendienteFacturas) }} de saldo pendiente.
                </p>
                <!-- El descuadre entre los dos saldos, con el texto que escribe el servidor: es una
                     inconsistencia que resuelve Administración y el informe no la esconde. -->
                <p class="conjunto-descuadre" v-if="conjunto.avisoDescuadre">
                  {{ conjunto.avisoDescuadre }}
                </p>
              </div>
              <p class="motivo" v-if="fila.conjuntosOmitidos > 0">
                Y {{ fila.conjuntosOmitidos }}
                {{ Number(fila.conjuntosOmitidos) === 1
                    ? 'conjunto más que no se enseña' : 'conjuntos más que no se enseñan' }}.
              </p>
            </template>

            <!-- #537 · QUÉ NO HA MIRADO LA BÚSQUEDA DEL CONJUNTO, en las DOS ramas y por eso aquí
                 fuera: el tope de facturas del cliente y las facturas sin «Total» positivo valen
                 igual en una fila que sí enseña un conjunto o unas candidatas, y es justo ahí donde
                 hace falta saberlo —un conjunto leído como si se hubiera mirado toda la cartera del
                 cliente es una propuesta que parece más firme de lo que es—. La frase la escribe el
                 servidor; aquí no se compone ninguna. -->
            <p class="aviso-busqueda" :class="{ 'aviso-tope': superaElTope(fila) }"
              v-if="fila.avisoBusquedaConjunto">
              {{ fila.avisoBusquedaConjunto }}
            </p>
          </td>
        </tr>
      </tbody>
      <tfoot v-if="totales">
        <tr>
          <td>{{ totales.abonos }} {{ Number(totales.abonos) === 1 ? 'abono' : 'abonos' }}</td>
          <td></td>
          <td></td>
          <td></td>
          <td class="num">{{ formatEuros(totales.importe) }}</td>
          <!-- La celda del total va vacía a propósito: los totales no se suman en ninguna parte del
               informe, porque la cifra de cabecera es la de los saldos. -->
          <td></td>
          <td></td>
          <td></td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<style scoped>
.tabla { width: 100%; border-collapse: collapse; font-size: 11px; }
.tabla th {
  background: #1a2744;
  color: #c8d3e5;
  font-weight: 600;
  padding: 6px 8px;
  text-align: left;
}
.tabla td { padding: 5px 8px; border-bottom: 1px solid #eef2f7; vertical-align: top; }
.tabla tfoot td { background: #F8FAFC; font-weight: 700; border-bottom: 0; }
.tabla .num { text-align: right; }

.vacio { margin: 6px 0 0; font-size: 12px; color: #64748b; }
.marca { font-size: 10px; font-weight: 700; }
.marca-aviso { color: #6B4A10; }
.motivo { margin: 2px 0 0; font-size: 10px; color: #64748b; line-height: 1.4; }

/* Lo que la búsqueda del conjunto no ha mirado. En gris cuando matiza un resultado y en rojo
   cuando el tope ha impedido calcularlo, que no es lo mismo (#537, regla 6). */
.aviso-busqueda { margin: 3px 0 0; font-size: 10px; color: #64748b; line-height: 1.4; }
.aviso-tope {
  padding: 4px 6px;
  color: #7A1B1B;
  background: #FDEDEE;
  border-radius: 4px;
}
.candidata { margin: 2px 0 0; font-size: 11px; color: #1a2744; }

/* El conjunto va en su propio bloque y con un filo a la izquierda: son varias facturas que se
   leen juntas, y suelta a suelta no se ve que son una sola propuesta. */
.conjunto {
  margin: 4px 0 0;
  padding: 4px 6px;
  background: #F8FAFC;
  border-left: 3px solid #1a2744;
}
.conjunto-titulo { margin: 0; font-size: 10px; font-weight: 700; color: #1a2744; }
.conjunto-sumas { margin: 2px 0 0; font-size: 10px; color: #64748b; line-height: 1.4; }
.conjunto-descuadre {
  margin: 3px 0 0;
  padding: 4px 6px;
  font-size: 10px;
  line-height: 1.4;
  color: #7A1B1B;
  background: #FDEDEE;
  border-radius: 4px;
}
</style>

<script>
/**
 * Una de las dos listas del informe de abonos abiertos (COB-7, #193): las filas y su pie de
 * totales.
 *
 * Es un componente y no dos tablas copiadas en la pantalla porque son **la misma tabla**: el bloque
 * de los que llevan más de un año y el del resto tienen las mismas columnas y las mismas salidas de
 * la columna de candidatas. Con la tabla escrita dos veces, un arreglo en una de ellas deja la otra
 * atrás, que es justo lo que pasa con los bloques que se parecen.
 *
 * **Solo pinta.** No decide nada: ni qué fila va en qué bloque —eso lo parte el servidor—, ni si una
 * sugerencia es única, varias o ninguna, que viene en `sugerencia`, ni qué facturas forman un
 * conjunto ni si sus saldos cuadran, que viene en `conjuntosSugeridos` con las sumas y el aviso ya
 * escritos (#537). Aquí no se suma ni se compara ningún importe: una cuenta repetida en la pantalla
 * es una cuenta que un día dice algo distinto de la del informe.
 */
export default {
  props: {
    /** Las filas de este bloque, ya ordenadas por el servidor (antigüedad e importe). */
    filas: {
      type: Array,
      default: function() { return []; }
    },
    /** `{abonos, importe}` de este bloque, para el pie. Nulo para no pintar pie. */
    totales: {
      type: Object,
      default: null
    }
  },
  methods: {
    /**
     * Si esta fila no tiene nada que proponer, **según el servidor**.
     *
     * Y no según el tamaño de `facturasSugeridas` ni de `conjuntosSugeridos`: una respuesta que
     * dijera `UNICA` con la lista vacía se rotularía «No deducible» contando la lista, o sea al
     * revés de lo que el informe ha decidido. El estado es el estado; las listas son lo que se
     * pinta debajo de él.
     */
    esNoDeducible: function(fila) {
      return fila.sugerencia === 'NO_DEDUCIBLE';
    },
    /** La candidata única se lee sola: es la que no necesita que se le ponga el rótulo delante. */
    esUnica: function(fila) {
      return fila.sugerencia === 'UNICA';
    },
    /**
     * Si el conjunto NO se ha buscado porque el cliente pasa del tope de facturas abiertas
     * (#537, regla 6).
     *
     * Solo decide el **color** del aviso: que no se haya calculado no es un matiz de un resultado,
     * es que no hay resultado. Lo que se lee es la frase que manda el servidor, con las palabras de
     * la regla 6 —«demasiadas facturas abiertas para deducir»—, y no un texto escrito aquí: el
     * correo pinta esa misma frase y así los dos lo cuentan igual.
     */
    superaElTope: function(fila) {
      return fila.topeFacturasSuperado === true;
    },
    /**
     * Una fecha del servidor (`2026-10-02`) como se escribe aquí: `02/10/2026`. Sin `new Date()`,
     * que interpreta la cadena en UTC y en Valladolid puede devolver el día de antes. Mismo
     * criterio que el Centro de Cobros.
     */
    formatFecha: function(valor) {
      if (!valor) return '—';
      const partes = String(valor).substring(0, 10).split('-');
      if (partes.length !== 3) return String(valor);
      return partes[2] + '/' + partes[1] + '/' + partes[0];
    },
    /**
     * Euros **con céntimos** y su unidad, o un guion SIN unidad cuando no hay dato.
     *
     * Con céntimos y no en euros enteros como el resto de la pestaña, y es una decisión: una
     * candidata se propone porque su saldo coincide EXACTAMENTE con el del abono, sin margen de
     * céntimos, así que quien la mira necesita ver los céntimos de los dos para darle crédito. Y de
     * paso la columna suma exactamente el pie, que es el criterio 1.
     *
     * El guion va sin unidad a propósito: con la unidad escrita en la plantilla, un hueco se pinta
     * «— €» y se lee como una cifra de cero euros (D-COB-8).
     */
    formatEuros: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '—';
      return Number(valor).toLocaleString('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) + ' €';
    },
    numero: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '—';
      return Number(valor).toLocaleString('es-ES');
    },
    texto: function(valor) {
      return valor === null || valor === undefined || valor === '' ? '—' : String(valor);
    },
    /**
     * Cómo se nombra un documento —el abono de la fila o una factura candidata—: su **código**, y
     * si no lo trae, su **número** con «nº» delante.
     *
     * Los documentos de antes de la numeración por código llegan sin `codigo` y solo con `numero`:
     * pintando el guion, la fila perdía el único identificador que había de ella, y esta lista está
     * para que Administración localice el abono y lo cierre. El guion queda cuando faltan los dos,
     * que ahí sí es verdad que no se sabe (D-COB-8).
     *
     * Mismo criterio y mismo orden que el correo del informe (`FactCobroAbonosHtml.rotuloDocumento`), para
     * que la pantalla y el correo nombren igual la misma fila.
     */
    documento: function(fila) {
      if (!fila) return '—';
      if (fila.codigo !== null && fila.codigo !== undefined && String(fila.codigo).trim() !== '') {
        return String(fila.codigo);
      }
      return fila.numero === null || fila.numero === undefined || fila.numero === ''
        ? '—'
        : 'nº ' + fila.numero;
    }
  }
};
</script>
